import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate, fmtDateTime } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useApi, Loading, ErrorNote, Panel, StatusChip, FileLinks, btn, inputCls, SampleBadge } from "../components/kit";
import { SdsSubmit } from "./Sds";

export function SdsReviewBox({ s, onDone }) {
  const [note, setNote] = useState("");
  const act = async (decision) => {
    try { await api.post(`/sds/${s.id}/review`, { decision, note }); toast.success(decision === "approved" ? "Approved and marked current." : "Sent back for correction."); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mt-4 space-y-3 border-t pt-4" data-testid={`sds-review-${s.id}`}>
      <label htmlFor={`n-${s.id}`} className="text-sm font-semibold">Reviewer note</label>
      <textarea id={`n-${s.id}`} className={inputCls} rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="sds-review-note" />
      <div className="flex flex-wrap gap-2">
        <button className={btn.primary} onClick={() => act("approved")} data-testid="sds-approve-button">Approve</button>
        <button className={btn.ghost} onClick={() => act("needs_correction")} data-testid="sds-correction-button">Needs correction</button>
      </div>
    </div>
  );
}

const EDITABLE = [["product_name", "Product name"], ["manufacturer", "Manufacturer"], ["product_identifier", "Product identifier"], ["location", "Workplace location"], ["revision_date", "SDS revision date"], ["language", "Language"]];

function CorrectForm({ s, onDone }) {
  const [f, setF] = useState(Object.fromEntries(EDITABLE.map(([k]) => [k, s[k] || ""])));
  const save = async (e) => {
    e.preventDefault();
    try { await api.patch(`/sds/${s.id}`, f); toast.success("Details corrected."); onDone(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={save} className="mt-4 grid gap-3 border-t pt-4 sm:grid-cols-2" data-testid="sds-correct-form">
      {EDITABLE.map(([k, l]) => (
        <label key={k} className="text-sm font-semibold">{l}
          <input type={k === "revision_date" ? "date" : "text"} className={inputCls} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`sds-correct-${k}`} /></label>))}
      <div className="sm:col-span-2"><button className={btn.dark} data-testid="sds-correct-save">Save corrected details</button></div>
    </form>
  );
}

function AiNote({ ai }) {
  if (!ai) return null;
  return (
    <div className={`mb-4 flex gap-2 rounded-lg p-3 text-sm ${ai.verified ? "bg-emerald-50 text-emerald-900" : "bg-violet-50 text-violet-900"}`} data-testid="sds-ai-note">
      <Sparkles className="h-4 w-4 shrink-0 mt-0.5" />
      <span>{ai.fields.length ? "Fields marked “AI-read” were read automatically from the pages." : "Details were read automatically but the submitter typed or changed all of them."}{" "}
        {ai.verified ? <b>Checked by {ai.verified_by} on approval.</b> : <b>Unverified — a reviewer must check them against the original.</b>}</span>
    </div>
  );
}

export default function SdsDetail() {
  const { sid } = useParams();
  const { user } = useAuth();
  const { data: s, loading, error, reload } = useApi(`/sds/${sid}`);
  const [editing, setEditing] = useState(false);
  if (loading && !s) return <Loading />;
  if (error) return <ErrorNote error={error} />;
  const reviewer = user.is_superadmin || (user.permissions || []).includes("sds_reviewer");
  const aiF = s.ai_extraction?.fields || [];
  const tag = (k) => aiF.includes(k) && <span className={`ml-2 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${s.ai_extraction.verified ? "bg-emerald-100 text-emerald-900" : "bg-violet-100 text-violet-900"}`} data-testid={`sds-detail-ai-${k}`}>{s.ai_extraction.verified ? "AI-read · checked" : "AI-read · unverified"}</span>;
  const rows = [["Product name", s.product_name, "product_name"], ["Manufacturer", s.manufacturer, "manufacturer"], ["Product identifier", s.product_identifier, "product_identifier"], ["Workplace location", s.location],
    ["SDS revision date", fmtDate(s.revision_date), "revision_date"], ["Language", s.language, "language"], ["Submitted by", s.submitted_by_name],
    ["Reviewer", s.reviewer_name || "Not reviewed yet"]];
  return (
    <div className="space-y-6">
      <Link to="/sds" className="inline-flex items-center gap-1 text-sm font-semibold text-muted-foreground"><ArrowLeft className="h-4 w-4" /> Safety Data Sheets</Link>
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-3xl font-semibold" data-testid="sds-detail-title">{s.product_name}</h1>
        <SampleBadge show={s.is_demo} />
        {s.is_current ? <StatusChip status="approved" label="Current approved version" /> : <StatusChip status={s.status} />}
      </div>
      <Panel title="Original document">
        <FileLinks files={s.files} testid="sds-page" />
        <p className="mt-3 text-xs text-muted-foreground">Read the original document for hazard and first-aid information. This portal does not summarise chemical safety instructions.</p>
      </Panel>
      <Panel title="Details">
        <AiNote ai={s.ai_extraction} />
        <dl className="grid gap-3 sm:grid-cols-2">{rows.map(([k, v, key]) => <div key={k}><dt className="text-xs uppercase tracking-wider text-muted-foreground">{k}</dt><dd className="font-medium">{v || "—"}{key && tag(key)}</dd></div>)}</dl>
        {reviewer && s.status !== "approved" && <button className="mt-4 text-sm font-semibold text-[color:var(--dcbc-red)]" onClick={() => setEditing(!editing)} data-testid="sds-correct-toggle">{editing ? "Close" : "Correct details"}</button>}
        {editing && <CorrectForm s={s} onDone={() => { setEditing(false); reload(); }} />}
        {(s.review_history || []).length > 0 && (
          <ul className="mt-4 space-y-1 border-t pt-3 text-sm">{s.review_history.map((h, i) => <li key={i}><b>{h.decision.replace("_", " ")}</b> by {h.reviewer_name}, {fmtDateTime(h.at)}{h.note && ` — “${h.note}”`}</li>)}</ul>
        )}
        {reviewer && s.status !== "approved" && <SdsReviewBox s={s} onDone={reload} />}
      </Panel>
      <Panel title="Versions" action={s.status === "approved" && <SdsSubmit group={s} onDone={reload} />}>
        <ul className="divide-y">{s.versions.map((v) => (
          <li key={v.id} className="flex items-center justify-between py-3">
            <Link to={`/sds/${v.id}`} className="font-medium underline-offset-2 hover:underline">Revision {fmtDate(v.revision_date)} · submitted {fmtDate(v.submitted_at)}</Link>
            {v.is_current ? <StatusChip status="approved" label="Current" /> : <StatusChip status={v.status === "approved" ? "closed" : v.status} label={v.status === "approved" ? "Older version" : undefined} />}
          </li>))}</ul>
      </Panel>
    </div>
  );
}
